import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

// Default range: first of the current month -> today (inclusive end).
function defaultRange() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(now) };
}

export default function TimeClock() {
  const [range, setRange] = useState(defaultRange());
  const [report, setReport] = useState([]);
  const [entries, setEntries] = useState([]);
  const [msg, setMsg] = useState('');

  // 'to' is an inclusive calendar day; the API treats `to` as exclusive, so
  // send the day after.
  function toExclusive(d) {
    if (!d) return '';
    const dt = new Date(d + 'T00:00:00');
    dt.setDate(dt.getDate() + 1);
    return dt.toISOString().slice(0, 10);
  }

  async function loadReport() {
    setMsg('');
    const qs = new URLSearchParams({ from: range.from, to: toExclusive(range.to) }).toString();
    const res = await apiFetch('/api/timeclock/report?' + qs);
    if (res.ok) { const d = await res.json(); setReport(d.report || []); }
    else { setMsg('Could not load report.'); }
  }
  async function loadEntries() {
    const qs = new URLSearchParams({ from: range.from, to: toExclusive(range.to) }).toString();
    const res = await apiFetch('/api/timeclock/entries?' + qs);
    if (res.ok) { const d = await res.json(); setEntries(d.entries || []); }
  }
  useEffect(() => { loadReport(); loadEntries(); /* eslint-disable-next-line */ }, []);

  function downloadCsv() {
    const qs = new URLSearchParams({ from: range.from, to: toExclusive(range.to) }).toString();
    const token = localStorage.getItem('hq_token');
    // Fetch with auth header, then trigger a client-side download.
    apiFetch('/api/timeclock/report.csv?' + qs)
      .then((res) => (res.ok ? res.blob() : Promise.reject()))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'timeclock-report.csv';
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => setMsg('Could not download CSV.'));
  }

  function fmt(s) { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch { return '—'; } }

  async function saveEntry(en, patch) {
    const res = await apiFetch(`/api/timeclock/entries/${en.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (res.ok) { loadEntries(); loadReport(); }
    else setMsg('Could not save entry.');
  }

  async function removeEntry(en) {
    if (!window.confirm('Delete this time entry? This cannot be undone.')) return;
    const res = await apiFetch(`/api/timeclock/entries/${en.id}`, { method: 'DELETE' });
    if (res.ok) { loadEntries(); loadReport(); }
  }

  return (
    <div className="hq-page-content">
      <div className="hq-page-head">
        <h1>Time Clock</h1>
        <p>Hours worked per person, and shift editing.</p>
      </div>

      <div className="hq-panel" style={{ marginBottom: 20 }}>
        <div className="hq-panel-head">
          <h2>Report</h2>
          <div className="hq-form" style={{ margin: 0 }}>
            <input className="hq-input" type="date" value={range.from}
              onChange={(e) => setRange({ ...range, from: e.target.value })} />
            <input className="hq-input" type="date" value={range.to}
              onChange={(e) => setRange({ ...range, to: e.target.value })} />
            <button className="hq-btn" onClick={() => { loadReport(); loadEntries(); }}>Run</button>
            <button className="hq-btn" onClick={downloadCsv}>Export CSV</button>
          </div>
        </div>
        <table className="hq-table">
          <thead><tr><th>Name</th><th>Email</th><th>Pay type</th><th>Shifts</th><th>Hours</th></tr></thead>
          <tbody>
            {report.map((r) => (
              <tr key={r.user_id}>
                <td>{`${r.first_name} ${r.last_name}`.trim() || '—'}</td>
                <td>{r.email}</td>
                <td className="hq-role">{r.pay_type}</td>
                <td>{r.shifts}</td>
                <td>{r.worked_hours}</td>
              </tr>
            ))}
            {report.length === 0 && (
              <tr><td className="hq-empty" colSpan={5}>No completed shifts in range.</td></tr>
            )}
          </tbody>
        </table>
        {msg && <p className="hq-msg">{msg}</p>}
      </div>

      <div className="hq-panel">
        <div className="hq-panel-head"><h2>Entries</h2></div>
        <table className="hq-table">
          <thead><tr><th>Name</th><th>Clock in</th><th>Clock out</th><th></th></tr></thead>
          <tbody>
            {entries.map((en) => (
              <tr key={en.id}>
                <td>{`${en.first_name} ${en.last_name}`.trim() || en.email}</td>
                <td>{fmt(en.clock_in)}</td>
                <td>{en.clock_out ? fmt(en.clock_out) : <span className="hq-pill hq-on">On duty</span>}</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="hq-signout" style={{ width: 'auto', padding: '0 12px', marginTop: 0 }}
                    onClick={() => {
                      const v = window.prompt('Set clock-out (ISO, e.g. 2026-06-04T17:30), or blank to leave open:', en.clock_out || '');
                      if (v === null) return;
                      saveEntry(en, { clock_out: v });
                    }}>Edit out</button>
                  {' '}
                  <button className="hq-signout" style={{ width: 'auto', padding: '0 12px', marginTop: 0, borderColor: 'rgba(255,90,82,.3)', color: '#ff5a52' }}
                    onClick={() => removeEntry(en)}>Delete</button>
                </td>
              </tr>
            ))}
            {entries.length === 0 && (
              <tr><td className="hq-empty" colSpan={4}>No entries in range.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
